import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { mockContactInfo, images } from '../data/mockData';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  Headphones,
} from 'lucide-react';

const ContactPage = () => {
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: 'Message Sent!',
      description: 'Thank you for contacting us. We will get back to you within 24 hours.',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        <img src={images.poolLounge} alt="Contact" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-cyan-800/70" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="contact-title">
              Contact Us
            </h1>
            <p className="text-lg text-blue-100 max-w-xl mx-auto">
              Get in touch with our team for bookings, memberships, or any questions
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border border-gray-200" data-testid="contact-info-card">
              <CardHeader>
                <CardTitle>Get In Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {[
                  {
                    icon: <MapPin className="h-5 w-5 text-cyan-600" />,
                    label: 'Address',
                    value: mockContactInfo.address,
                    bg: 'from-cyan-50 to-teal-50',
                  },
                  {
                    icon: <Phone className="h-5 w-5 text-green-600" />,
                    label: 'Phone',
                    value: mockContactInfo.phone,
                    bg: 'from-green-50 to-emerald-50',
                  },
                  {
                    icon: <Mail className="h-5 w-5 text-purple-600" />,
                    label: 'Email',
                    value: mockContactInfo.email,
                    bg: 'from-purple-50 to-pink-50',
                  },
                  {
                    icon: <Headphones className="h-5 w-5 text-orange-600" />,
                    label: 'Support',
                    value: mockContactInfo.support,
                    bg: 'from-orange-50 to-red-50',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.bg} flex items-center justify-center flex-shrink-0`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{item.label}</h3>
                      <p className="text-gray-600 text-sm">{item.value}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border border-gray-200" data-testid="hours-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-cyan-600" />
                  Operating Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-900 text-sm">Monday - Friday</span>
                  <span className="text-gray-600 text-sm">{mockContactInfo.hours.weekdays}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-900 text-sm">Saturday - Sunday</span>
                  <span className="text-gray-600 text-sm">{mockContactInfo.hours.weekends}</span>
                </div>
              </CardContent>
            </Card>

            {/* Google Maps Placeholder */}
            <Card className="border border-gray-200 overflow-hidden" data-testid="map-card">
              <CardHeader>
                <CardTitle>Find Us</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="w-full h-56 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center space-y-2">
                    <MapPin className="h-10 w-10 text-gray-400 mx-auto" />
                    <p className="text-gray-500 font-medium text-sm">Google Maps</p>
                    <p className="text-xs text-gray-400">Map integration placeholder</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="border border-gray-200 h-fit" data-testid="contact-form">
            <CardHeader>
              <CardTitle>Send Us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm">First Name</Label>
                    <Input id="firstName" placeholder="First name" required data-testid="input-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm">Last Name</Label>
                    <Input id="lastName" placeholder="Last name" required data-testid="input-last-name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <Input id="email" type="email" placeholder="Email address" required data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                  <Input id="phone" type="tel" placeholder="Phone number" data-testid="input-phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm">Subject</Label>
                  <Input id="subject" placeholder="What is this about?" required data-testid="input-subject" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm">Message</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us more..."
                    className="min-h-28"
                    required
                    data-testid="input-message"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  size="lg"
                  data-testid="send-message-btn"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Quick Contact Actions */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-cyan-500 to-blue-500 border-0 text-white hover:shadow-xl transition-all cursor-pointer">
            <CardContent className="p-6 text-center space-y-2">
              <Phone className="h-7 w-7 mx-auto" />
              <h3 className="font-semibold">Call Us Now</h3>
              <p className="text-cyan-100 text-sm">Speak with our team</p>
              <Button variant="secondary" size="sm" className="bg-white text-cyan-600 hover:bg-gray-100">
                {mockContactInfo.phone}
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-500 border-0 text-white hover:shadow-xl transition-all cursor-pointer">
            <CardContent className="p-6 text-center space-y-2">
              <Mail className="h-7 w-7 mx-auto" />
              <h3 className="font-semibold">Email Us</h3>
              <p className="text-green-100 text-sm">Send your questions</p>
              <Button variant="secondary" size="sm" className="bg-white text-green-600 hover:bg-gray-100">
                Send Email
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-500 border-0 text-white hover:shadow-xl transition-all cursor-pointer">
            <CardContent className="p-6 text-center space-y-2">
              <Headphones className="h-7 w-7 mx-auto" />
              <h3 className="font-semibold">Support Line</h3>
              <p className="text-purple-100 text-sm">Immediate assistance</p>
              <Button variant="secondary" size="sm" className="bg-white text-purple-600 hover:bg-gray-100">
                {mockContactInfo.support}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
